<template>
  <span class="m-pill" :class="`m-pill--${resolvedTone}`">{{ label || status }}</span>
</template>

<script>
/**
 * MusubiStatusPill — a small coloured status chip. Maps common Musubi status
 * keywords to a tone automatically; override with the `tone` prop.
 *
 *   <MusubiStatusPill status="qualified" />
 *   <MusubiStatusPill status="cold" label="Cold lead" />
 *   <MusubiStatusPill status="Custom" tone="accent" />
 *
 * Tones: success | warning | danger | info | warm | accent | neutral
 */
const TONE_MAP = {
  // success
  sent: 'success', delivered: 'success', live: 'success', qualified: 'success',
  done: 'success', active: 'success', found: 'success', completed: 'success',
  // warning / in-progress
  scheduled: 'warning', sending: 'warning', qualifying: 'warning', queued: 'warning', leased: 'warning',
  // danger / terminal-bad
  failed: 'danger', bounced: 'danger', rejected: 'danger', complained: 'danger', error: 'danger',
  // info / temperature
  cold: 'info', warm: 'warm',
  // accent / promotion
  promoted: 'accent', routed: 'accent',
  // discovery — "gathering, not sending yet" (deliberately its own teal tone,
  // NOT the paused/neutral grey, so the two read as distinct states).
  discovering: 'discovery',
  // neutral / inert
  draft: 'neutral', new: 'neutral', paused: 'neutral', archived: 'neutral', none: 'neutral',
};

export default {
  name: 'MusubiStatusPill',
  props: {
    status: { type: String, required: true },
    /** Override display text (defaults to the status). */
    label:  { type: String, default: '' },
    /** Force a tone instead of auto-mapping. */
    tone:   { type: String, default: '' },
  },
  computed: {
    resolvedTone() {
      return this.tone || TONE_MAP[(this.status || '').toLowerCase()] || 'neutral';
    },
  },
};
</script>

<style scoped>
.m-pill {
  display: inline-block; font-size: 0.7rem; font-weight: 700;
  border-radius: 20px; padding: 2px 9px; line-height: 1.5; white-space: nowrap;
}
.m-pill--success { background: color-mix(in srgb, var(--m-status-success) 16%, #fff); color: var(--m-status-success); }
.m-pill--warning { background: color-mix(in srgb, var(--m-status-warning) 18%, #fff); color: #92600a; }
.m-pill--danger  { background: color-mix(in srgb, var(--m-status-danger) 14%, #fff);  color: var(--m-status-danger); }
.m-pill--info    { background: color-mix(in srgb, var(--m-status-info) 14%, #fff);    color: var(--m-status-info); }
.m-pill--warm    { background: #ffe9ee; color: #e11d48; }
.m-pill--accent  { background: var(--m-accent-soft); color: var(--m-accent); }
.m-pill--discovery { background: #CCFBF1; color: #115E59; }
.m-pill--neutral { background: var(--m-bg-content); color: var(--m-text-secondary); border: 1px solid var(--m-border); }
</style>
